import React, { useState, useMemo } from 'react';
import FeatureTagSelector from '../FeatureTags/FeatureTagSelector';
import TaskTypeSelect from '../shared/TaskTypeSelect';
import { DEFAULT_TASK_TYPE, TASK_TYPE_SUBTASK } from '../../constants/taskTypes';
import './TaskForm.css';

const TaskForm = ({ onSuccess, onCancel, users, isProjectTask, projectId, onTagCreate, availableTags = [], titleInputRef, defaultTaskType = DEFAULT_TASK_TYPE, parentTask, onSwitchToStandalone }) => {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        assigned_to: '',
        feature_tag_id: null,
        task_type: defaultTaskType
    });
    
    const [files, setFiles] = useState([]);
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // Filter out users with role "unknown"
    const filteredUsers = useMemo(() => {
        return users ? users.filter(user => user.role !== 'unknown') : [];
    }, [users]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files);
        setFiles(prev => [...prev, ...selectedFiles]);
    };

    const removeFile = (index) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    };

    // Helper function to get due date info for selected tag
    const getSelectedTagDueDate = () => {
        if (!formData.feature_tag_id) return null;
        const selectedTag = availableTags.find(tag => tag.id === formData.feature_tag_id);
        return selectedTag?.due_date || null;
    };

    // Helper function to format due date for display
    const formatDueDateForDisplay = (dueDate) => {
        if (!dueDate) return null;
        const date = new Date(dueDate);
        const now = new Date();
        const diffTime = date - now;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays < 0) {
            return { text: `${Math.abs(diffDays)} days overdue`, status: 'overdue' };
        } else if (diffDays === 0) {
            return { text: 'Due today', status: 'due-today' };
        } else if (diffDays <= 3) {
            return { text: `${diffDays} days left`, status: 'urgent' };
        } else if (diffDays <= 7) {
            return { text: `${diffDays} days left`, status: 'upcoming' };
        } else {
            return { text: `Due ${date.toLocaleDateString()}`, status: 'normal' };
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setError('');
        setSubmitting(true);

        try {
            // Validate required fields
            if (!formData.title.trim()) {
                throw new Error('Title is required');
            }

            console.log('Starting form submission with files:', files);

            // Create FormData object
            const submitData = new FormData();
            
            // Add basic task data
            submitData.append('title', formData.title);
            submitData.append('description', formData.description);
            submitData.append('task_type', formData.task_type);
            if (formData.assigned_to) {
                submitData.append('assigned_to', formData.assigned_to);
            }
            if (formData.project_id) {
                submitData.append('project_id', formData.project_id);
            }
            if (formData.feature_tag_id) {
                submitData.append('feature_tag_id', formData.feature_tag_id);
            }

            // Add files
            if (files.length > 0) {
                console.log('Appending files to FormData:', files);
                files.forEach((file, index) => {
                    console.log(`Appending file ${index}:`, file.name, file.type, file.size);
                    submitData.append('files', file);
                });
            }

            // Log FormData contents
            console.log('FormData contents:');
            for (let pair of submitData.entries()) {
                console.log(pair[0], pair[1]);
            }

            console.log('Calling onSuccess with FormData');
            await onSuccess(submitData);
            console.log('Form submission successful');
            
            setFormData({
                title: '',
                description: '',
                assigned_to: '',
                feature_tag_id: null,
                task_type: defaultTaskType
            });
            setFiles([]);
        } catch (err) {
            console.error('Form submission error:', err);
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const isSubtaskMode = !!parentTask;

    return (
        <form onSubmit={handleSubmit} className="task-form">
            {error && (
                <div className="error-message">
                    {error}
                </div>
            )}

            {isSubtaskMode && parentTask && (
                <div className="task-form-subtask-banner">
                    <p className="task-form-subtask-banner__message">
                        Creating a subtask for <strong>{parentTask.display_id ? `[${parentTask.display_id}]` : ''} {parentTask.title || 'Untitled'}</strong>. Subtasks break down work into smaller, trackable pieces under a Story or Task.
                    </p>
                    <p className="task-form-subtask-banner__type-note">
                        Task type is fixed to &quot;{TASK_TYPE_SUBTASK}&quot; because this will be linked to the parent above.
                    </p>
                    {onSwitchToStandalone && (
                        <button
                            type="button"
                            className="task-form-subtask-banner__switch"
                            onClick={onSwitchToStandalone}
                        >
                            Create as standalone task instead
                        </button>
                    )}
                </div>
            )}

            <div className="form-group">
                <label htmlFor="title">Title *</label>
                <input
                    ref={titleInputRef}
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Enter task title"
                    className="form-input"
                />
            </div>

            <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Enter task description"
                    className="form-textarea"
                    rows="4"
                />
            </div>

            <div className="form-group">
                <label htmlFor="task_type">Task Type</label>
                <TaskTypeSelect
                    id="task_type"
                    name="task_type"
                    value={formData.task_type}
                    onChange={handleChange}
                    className="form-select"
                    includeOnlyTypes={isSubtaskMode ? [TASK_TYPE_SUBTASK] : undefined}
                    excludeTypes={!isSubtaskMode ? [TASK_TYPE_SUBTASK] : []}
                />
                {isSubtaskMode && (
                    <span className="task-form__field-hint">Only {TASK_TYPE_SUBTASK} is available when creating a linked subtask.</span>
                )}
            </div>

            {isProjectTask && (
                <>
                    <div className="form-group">
                        <label htmlFor="assigned_to">Assign To</label>
                        <select
                            id="assigned_to"
                            name="assigned_to"
                            value={formData.assigned_to}
                            onChange={handleChange}
                            className="form-select"
                        >
                            <option value="">Unassigned</option>
                            {filteredUsers.map(user => (
                                <option key={user.id} value={user.id}>
                                    {user.display_name || user.email}
                                </option>
                            ))}
                        </select>
                    </div>

                    {projectId && (
                        <div className="form-group">
                            <label>Feature Tag</label>
                            <FeatureTagSelector
                                projectId={projectId}
                                selectedTagId={formData.feature_tag_id}
                                onTagSelect={(tagId) => setFormData(prev => ({ ...prev, feature_tag_id: tagId }))}
                                onTagCreate={onTagCreate}
                                placeholder="Select a feature tag (optional)"
                            />
                            
                            {/* Due Date Information */}
                            {(() => {
                                const dueDate = getSelectedTagDueDate();
                                const dueDateInfo = formatDueDateForDisplay(dueDate);
                                
                                if (dueDateInfo) {
                                    return (
                                        <div className={`task-form__due-date-info task-form__due-date-info--${dueDateInfo.status}`}>
                                            <span className="task-form__due-date-icon">
                                                {dueDateInfo.status === 'overdue' && '⚠️'}
                                                {dueDateInfo.status === 'due-today' && '🕐'}
                                                {dueDateInfo.status === 'urgent' && '⏰'}
                                                {dueDateInfo.status === 'upcoming' && '📅'}
                                                {dueDateInfo.status === 'normal' && '📅'}
                                            </span>
                                            <span className="task-form__due-date-text">{dueDateInfo.text}</span>
                                        </div>
                                    );
                                }
                                return null;
                            })()}
                        </div>
                    )}
                </>
            )}

            <div className="form-group">
                <label htmlFor="files">Attachments</label>
                <div className="file-upload-container">
                    <input
                        type="file"
                        id="files"
                        multiple
                        onChange={handleFileChange}
                        className="file-input"
                    />
                    <label htmlFor="files" className="file-input-label">
                        <span>Add Files</span>
                    </label>
                </div>
                {files.length > 0 && (
                    <div className="file-list">
                        {files.map((file, index) => (
                            <div key={index} className="file-item">
                                <span className="file-name">{file.name}</span>
                                <button
                                    type="button"
                                    onClick={() => removeFile(index)}
                                    className="remove-file-button"
                                >
                                    ×
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="form-actions">
                <button
                    type="button"
                    onClick={onCancel}
                    className="cancel-button"
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    className="submit-button"
                    disabled={submitting}
                >
                    {submitting ? 'Creating...' : (isSubtaskMode ? 'Create Subtask' : 'Create Task')}
                </button>
            </div>
        </form>
    );
};

export default TaskForm; 